<template>
    <Modal v-show="isModalOpen" :isOpen="isModalOpen" title="Topic Messages Filter" size="xl" @update:isOpen="handleClose">
        <template #default>
            <div class="space-y-5">
                <!-- Seek Filter Section -->
                <div class="border-b pb-6">
                    <h3 class="text-md font-semibold mb-4">Seek Filter</h3>
                    <div class="space-y-3">
                        <!-- Input Group for Seek Filter -->
                        <div class="flex gap-3">
                            <Select v-model="seekType">
                                <SelectTrigger>
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem v-for="option in seekTypeOptions" :key="option.value" :value="option.value">
                                        {{ option.name }}
                                    </SelectItem>
                                </SelectContent>
                            </Select>

                            <div class="flex-1">
                                <template v-if="seekType === 'offset'">
                                    <Input type="number" v-model.number="seekValue" placeholder="Enter offset" />
                                </template>
                                <template v-else-if="seekType === 'timestamp'">
                                    <DatePicker v-model="seekValue" placeholder="Select timestamp" class="w-full" />
                                </template>
                            </div>
                        </div>
                        <div v-if="seekError" class="text-destructive text-xs">
                            {{ seekError }}
                        </div>
                    </div>
                </div>

                <!-- Regex Filter Section -->
                <div>
                    <h3 class="text-md font-semibold mb-4">Regex Filter</h3>
                    <div class="space-y-4">
                        <div>
                            <Label for="search-fields">Search In:</Label>
                            <MultiSelect
                                id="search-fields"
                                v-model="selectedFieldNames"
                                :options="fieldOptions"
                                placeholder="Select fields"
                                :option-label="'name'"
                                :track-by="'name'"
                                class="w-full mt-2" />
                            <div v-if="fieldError" class="text-destructive text-xs mt-1">
                                {{ fieldError }}
                            </div>
                        </div>

                        <div>
                            <Label for="regex-pattern">Regex Pattern:</Label>
                            <Codemirror
                                id="regex-pattern"
                                v-model="pattern"
                                :extensions="cmExtensions"
                                :autofocus="false"
                                :indent-with-tab="false"
                                :tab-size="2"
                                class="w-full border rounded-sm mt-2" />
                            <div v-if="patternError" class="text-destructive text-xs mt-1">
                                {{ patternError }}
                            </div>
                        </div>
                    </div>

                    <Alert v-if="selectedFields.length && pattern?.length" class="mt-4" variant="warning">
                        <AlertTriangle class="h-4 w-4" />
                        <AlertDescription class="text-sm">
                            Regex search is not natively supported by Kafka and requires scanning messages in a less performant way. This may take
                            longer or timeout if the topic contains a large number of messages.
                        </AlertDescription>
                    </Alert>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end gap-3">
                <Button @click="handleCancel" variant="secondary">Cancel</Button>
                <Button @click="applyFilter" :disabled="!isFormValid" class="bg-primary hover:bg-primary"> Apply </Button>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useColorMode } from '@vueuse/core';
import Modal from '@/components/global/modals/Modal.vue';
import { MultiSelect } from '@/components/ui/multi-select';
import { Codemirror } from 'vue-codemirror';

// CM6 bits
import { basicSetup } from 'codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import { javascript } from '@codemirror/lang-javascript';
import { EditorView } from '@codemirror/view';

import { DatePicker } from '@/components/ui/date-picker';
import { AlertTriangle } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    },
    initialFilter: {
        type: Object,
        default: () => ({
            regex: { fields: [], pattern: '' },
            seek: { seekType: null, seekValue: null }
        })
    }
});
const emit = defineEmits(['apply', 'update:isOpen']);

// Theme management (reactive)
const colorMode = useColorMode();

// Modal open state
const isModalOpen = ref(props.isOpen);

// Seek filter
const seekType = ref(props.initialFilter?.seek?.seekType ?? null);
const seekValue = ref(props.initialFilter?.seek?.seekValue ?? null);
const seekTypeOptions = ref([
    { name: 'Offset', value: 'offset' },
    { name: 'Timestamp', value: 'timestamp' }
]);
const seekError = computed(() => {
    if (seekType.value && (seekValue.value === null || seekValue.value === '')) {
        return 'Please provide a value for the seek filter.';
    }
    return '';
});

watch(
    () => seekType.value,
    (newVal) => {
        if (newVal === 'timestamp') {
            if (props.initialFilter?.seek?.seekValue) {
                if (props.initialFilter?.seek?.seekType === 'timestamp') {
                    seekValue.value = new Date(props.initialFilter?.seek?.seekValue);
                } else {
                    seekValue.value = new Date();
                }
            } else {
                seekValue.value = new Date();
            }
        } else if (newVal === 'offset') {
            if (props.initialFilter?.seek?.seekValue) {
                if (props.initialFilter?.seek?.seekType === 'offset') {
                    seekValue.value = props.initialFilter?.seek?.seekValue;
                } else {
                    seekValue.value = null;
                }
            } else {
                seekValue.value = null;
            }
        }
    }
);

// Regex filter
const fieldOptions = [{ name: 'key' }, { name: 'value' }];
const selectedFields = ref((props.initialFilter?.regex?.fields || []).map((field) => ({ name: field })));
const selectedFieldNames = computed({
    get() {
        return selectedFields.value.map((field) => field.name);
    },
    set(names) {
        selectedFields.value = names.map((name) => fieldOptions.find((option) => option.name === name)).filter(Boolean);
    }
});
const pattern = ref(props.initialFilter?.regex?.pattern || '');

// --- CodeMirror 6 EXTENSIONS (the important part) ---
const cmExtensions = computed(() => {
    const extensions = [
        basicSetup, // sensible defaults
        javascript(), // replaces old `mode: 'javascript'`
        EditorView.lineWrapping // replaces old `lineWrapping: true`
    ];
    if (colorMode.value === 'dark') {
        extensions.push(oneDark); // apply dark theme dynamically
    }
    return extensions;
});
// ----------------------------------------------------

// Validation
const fieldError = computed(() => {
    if (selectedFields.value.length === 0 && pattern.value.trim() !== '') {
        return 'At least one field must be selected.';
    }
    return '';
});
const patternError = computed(() => {
    if (pattern.value.trim() === '' && selectedFields.value.length > 0) {
        return 'Regex pattern must not be empty.';
    }
    if (pattern.value.trim() !== '') {
        try {
            new RegExp(pattern.value);
            return '';
        } catch (e) {
            return 'Invalid regex pattern.';
        }
    }
    return '';
});

const isSeekValid = computed(() => seekType.value && seekValue.value !== null && seekError.value === '');
const isRegexValid = computed(() => {
    return selectedFields.value.length > 0 && pattern.value && pattern.value?.trim() !== '' && patternError.value === '';
});
const isFormValid = computed(() => isSeekValid.value || isRegexValid.value);

// Actions
const applyFilter = () => {
    if (!isFormValid.value) return;

    const payload = {};

    if (isSeekValid.value) {
        payload.seek = {
            seekType: seekType.value,
            seekValue: seekType.value === 'timestamp' && seekValue.value ? new Date(seekValue.value).getTime() : seekValue.value
        };
    }

    if (isRegexValid.value) {
        payload.regex = {
            fields: selectedFields.value.map((item) => item.name),
            pattern: pattern.value?.trim()
        };
    }

    emit('apply', payload);
};

const handleClose = (value) => {
    isModalOpen.value = value;
    if (!value) emit('update:isOpen', false);
};

const handleCancel = () => {
    emit('update:isOpen', false);
};

watch(
    () => props.initialFilter,
    (newVal) => {
        selectedFields.value = (newVal?.regex?.fields || []).map((field) => ({ name: field }));
        pattern.value = newVal?.regex?.pattern || '';
        seekType.value = newVal?.seek?.seekType ?? 'offset';
        seekValue.value = newVal?.seek?.seekValue;
    },
    { immediate: true }
);

watch(
    () => props.isOpen,
    (v) => {
        isModalOpen.value = v;
    }
);
</script>

<style scoped></style>
